import { useTranslation } from 'react-i18next';
import { useClientId } from '../hooks/useClientId';
import { useCompliance, useComplianceDeadlines } from '../hooks/useCompliance';
import { useActivities } from '../hooks/useActivities';
import { ApiErrorMessage } from '@/components/feedback/ApiErrorMessage';
import { ScoreBar } from '@/components/ui/ScoreBar';
import { formatDate } from '../format';

export function CompliancePage() {
  const { t, i18n } = useTranslation();
  const clientId = useClientId();

  const { data: complianceData, isLoading, isError, error, refetch } = useCompliance(clientId);
  const { data: deadlinesData } = useComplianceDeadlines(clientId);
  const { data: recentActivitiesData } = useActivities(clientId, { period: 'quarter', pageSize: 5 });

  if (isError) {
    return <ApiErrorMessage error={error} onRetry={() => refetch()} />;
  }

  const compliance = complianceData?.data;
  const deadlines = deadlinesData?.data ?? [];

  const overallBannerStyle = compliance?.overallStatus === 'compliant'
    ? { bg: 'var(--success-light)', color: 'var(--success-dark)', icon: 'verified' }
    : compliance?.overallStatus === 'attention_needed'
    ? { bg: 'var(--warning-light)', color: 'var(--warning)', icon: 'warning' }
    : { bg: 'var(--destructive-light)', color: 'var(--destructive)', icon: 'error' };

  const ppwrLabel = compliance?.ppwr.status === 'prepared' ? t('compliance.prepared')
    : compliance?.ppwr.status === 'mostly_prepared' ? t('compliance.mostlyPrepared')
    : t('compliance.actionNeeded');

  const ppwrDeadline = compliance?.ppwr.deadlineDate
    ? new Date(compliance.ppwr.deadlineDate).toLocaleDateString(i18n.language, { month: 'long', year: 'numeric' })
    : '';
  const ppwrDescription = compliance?.ppwr.status === 'prepared'
    ? t('compliance.ppwrDescPrepared')
    : t('compliance.ppwrDescAttention');

  return (
    <div className="space-y-5">
      <div>
        <h1 style={{ fontSize: '1.25rem', fontWeight: 700, letterSpacing: '-0.02em', color: 'var(--foreground-heading)' }}>
          {t('compliance.title')}
        </h1>
        <p className="mt-[3px]" style={{ fontSize: '0.8125rem', color: 'var(--foreground-muted)' }}>
          {t('compliance.subtitle')}
        </p>
      </div>

      {isLoading ? (
        <div className="space-y-4">
          <div className="skeleton h-16 rounded-xl" />
          <div className="skeleton h-64 rounded-xl" />
          <div className="skeleton h-32 rounded-xl" />
        </div>
      ) : compliance && (
        <>
          {/* Overall banner */}
          <div
            className="flex items-center gap-3 rounded-xl px-5 py-4"
            style={{ backgroundColor: overallBannerStyle.bg }}
          >
            <span className="material-symbols-outlined" style={{ fontSize: '28px', color: overallBannerStyle.color }}>{overallBannerStyle.icon}</span>
            <div>
              <p className="text-[0.9375rem] font-semibold" style={{ color: overallBannerStyle.color }}>
                {compliance.overallStatus === 'compliant' ? t('compliance.allGood') : compliance.overallStatus === 'attention_needed' ? t('compliance.attentionNeeded') : t('compliance.actionRequired')}
              </p>
              <p className="text-xs" style={{ color: overallBannerStyle.color, opacity: 0.8 }}>
                {t('compliance.risksCovered', { count: compliance.risksCoveredCount })}
              </p>
            </div>
          </div>

          {/* Compliance status card */}
          <div className="rounded-xl overflow-hidden" style={{ backgroundColor: 'var(--background)', border: '1px solid var(--border-soft)', boxShadow: 'var(--card-shadow)' }}>
            {/* PPWR */}
            <div className="px-5 py-4" style={{ borderBottom: '1px solid var(--border-soft)' }}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="material-symbols-outlined" style={{ fontSize: '18px', color: compliance.ppwr.status === 'prepared' ? 'var(--success-dark)' : 'var(--warning)' }}>
                    {compliance.ppwr.status === 'prepared' ? 'check_circle' : 'warning'}
                  </span>
                  <p className="text-[0.8125rem] font-semibold" style={{ color: 'var(--foreground)' }}>PPWR</p>
                </div>
                <span className="rounded-full px-2.5 py-0.5 text-[0.6875rem] font-medium" style={{
                  backgroundColor: compliance.ppwr.status === 'prepared' ? 'var(--success-light)' : 'var(--warning-light)',
                  color: compliance.ppwr.status === 'prepared' ? 'var(--success-dark)' : 'var(--warning)',
                }}>{ppwrLabel}</span>
              </div>
              <p className="mt-1.5 text-xs" style={{ color: 'var(--foreground-muted)' }}>{ppwrDescription} ({t('compliance.deadline')}: {ppwrDeadline})</p>
              <div className="mt-2">
                <ScoreBar score={compliance.ppwr.packInfoScore} variant="compact" />
              </div>
            </div>

            {/* GS1 */}
            <div className="px-5 py-4" style={{ borderBottom: '1px solid var(--border-soft)' }}>
              <div className="flex items-center gap-2">
                <span className="material-symbols-outlined" style={{ fontSize: '18px', color: 'var(--success-dark)' }}>check_circle</span>
                <p className="text-[0.8125rem] font-semibold" style={{ color: 'var(--foreground)' }}>GS1</p>
              </div>
              <p className="mt-1.5 text-xs" style={{ color: 'var(--foreground-muted)' }}>
                {t('compliance.gs1Version', { version: compliance.gs1.currentVersion })} &middot; {t('compliance.gs1LastUpdate', { update: compliance.gs1.latestUpdate })} &middot; {compliance.gs1.fieldsAffected} {t('compliance.fieldsAffected')}
              </p>
            </div>

            {/* Retailer profiles */}
            <div className="px-5 py-4">
              <div className="flex items-center gap-2">
                <span className="material-symbols-outlined" style={{ fontSize: '18px', color: 'var(--success-dark)' }}>check_circle</span>
                <p className="text-[0.8125rem] font-semibold" style={{ color: 'var(--foreground)' }}>{t('compliance.retailerProfiles')}</p>
              </div>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {compliance.retailerProfiles.filter(p => p.active).map((p) => (
                  <span key={p.id} className="rounded-full px-2.5 py-0.5 text-xs font-medium" style={{ backgroundColor: 'var(--primary-light)', color: 'var(--primary)' }}>
                    {p.retailerName}
                  </span>
                ))}
                {compliance.retailerProfiles.filter(p => p.active).length === 0 && (
                  <span className="text-xs" style={{ color: 'var(--foreground-muted)' }}>{t('compliance.noRetailers')}</span>
                )}
              </div>
            </div>
          </div>

          {/* Recent compliance actions */}
          {(() => {
            const complianceActions = (recentActivitiesData?.data ?? []).filter(
              a => a.type === 'gs1_update' || a.type === 'legislation_change' || a.type === 'packaging_update',
            );
            if (complianceActions.length === 0) return null;
            return (
              <div>
                <h2 className="mb-3 text-[0.8125rem] font-semibold" style={{ color: 'var(--foreground-heading)' }}>{t('compliance.recentActions')}</h2>
                <div className="space-y-2">
                  {complianceActions.map((action) => (
                    <div key={action.id} className="flex items-start gap-3 rounded-xl px-4 py-3" style={{ backgroundColor: 'var(--background)', border: '1px solid var(--border-soft)' }}>
                      <span className="material-symbols-outlined mt-0.5" style={{ fontSize: '16px', color: 'var(--secondary)' }}>
                        {action.type === 'gs1_update' ? 'update' : action.type === 'legislation_change' ? 'gavel' : 'package_2'}
                      </span>
                      <div>
                        <p className="text-[0.8125rem] font-medium" style={{ color: 'var(--foreground)' }}>{action.title}</p>
                        {action.description && <p className="text-xs" style={{ color: 'var(--foreground-muted)' }}>{action.description}</p>}
                        <p className="mt-0.5 text-[0.6875rem]" style={{ color: 'var(--foreground-muted)' }}>{formatDate(action.createdAt, i18n.language)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}

          {/* Deadlines */}
          {deadlines.length > 0 && (
            <div>
              <h2 className="mb-3 text-[0.8125rem] font-semibold" style={{ color: 'var(--foreground-heading)' }}>{t('compliance.deadlines')}</h2>
              <div className="space-y-2">
                {deadlines.map((dl) => (
                  <div key={dl.id} className="flex items-start gap-3 rounded-xl px-4 py-3" style={{ backgroundColor: 'var(--background)', border: '1px solid var(--border-soft)' }}>
                    <span className="material-symbols-outlined mt-0.5" style={{
                      fontSize: '18px',
                      color: dl.status === 'overdue' ? 'var(--destructive)' : dl.status === 'attention_needed' ? 'var(--warning)' : 'var(--secondary)',
                    }}>
                      {dl.status === 'overdue' ? 'error' : dl.status === 'attention_needed' ? 'warning' : 'schedule'}
                    </span>
                    <div>
                      <p className="text-[0.8125rem] font-medium" style={{ color: 'var(--foreground)' }}>{dl.title}</p>
                      <p className="text-xs" style={{ color: 'var(--foreground-muted)' }}>{dl.deadlineDate} &middot; {dl.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
